import User from '@/types/user';
import * as Sentry from '@sentry/browser';

const LOGIN_CONFLICT_ERROR = {
  reason: '409: email conflict',
};

/**
 * Initialize the Sentry plugin
 */
const init = () => {
  Sentry.init({
    dsn: import.meta.env.REACT_APP_SENTRY_DSN,
    release: import.meta.env.REACT_APP_VERSION,
    environment: import.meta.env.REACT_APP_SENTRY_ENV,
    beforeSend(event, hint) {
      /**
       * Prevent sending any 4xx error to Sentry. They most likely come from unhandled promise
       * rejections and do not provide any valuable information. There is no easy way to catch
       * all unhandled rejections while also being able to handle them manually when needed, so
       * the easiest is to catch them here.
       */
      const status: any = hint && hint.originalException && (hint.originalException as any).status;
      if (status && status >= 400 && status < 500) {
        return null;
      }

      return event;
    },
  });
};

/**
 * Set the current user's identity in the Sentry plugin
 * @param user User to identify
 */
const identify = (user: User) => {
  const { id, email } = user;
  Sentry.configureScope((scope: Sentry.Scope) => {
    scope.setUser({ id, email });
  });
};

/**
 * Set the current user's session url in the Sentry plugin.
 * @param sessionUrl User's session url.
 */
const setSessionUrl = (sessionUrl: string) => {
  Sentry.configureScope((scope: Sentry.Scope) => {
    scope.setExtra('sessionURL', sessionUrl);
  });
};

/**
 * Send a Sentry error report
 * @param error Error to send to Sentry
 * @param info Keyed object to attach extra info to the Sentry report
 */
const captureError = (error: Error, info = {}, level: Sentry.Severity = Sentry.Severity.Error): Promise<string> =>
  new Promise(resolve => {
    Sentry.withScope((scope: Sentry.Scope) => {
      scope.setExtras(info);
      scope.setLevel(level);
      const eventId = Sentry.captureException(error);
      return resolve(eventId);
    });
  });

const isUserConflictError = (error: any) => error && error.reason === LOGIN_CONFLICT_ERROR.reason;

export default { init, identify, isUserConflictError, captureError, setSessionUrl };
